import type { ReactElement } from "react";
import type { VsmPlanStatus } from "@playbookai/types";
import { VSM_JIRA_UI_ENABLED } from "@playbookai/types";
import type { KaizenLinkedWork, KaizenRow } from "@playbookai/widget-schemas";
import { KAIZEN_LIST_ROW_LIMIT } from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/index.js";
import { Link } from "../../atoms/Link/index.js";
import { StatusDot } from "../../atoms/StatusDot/index.js";
import { safeExternalHref } from "../../lib/external-href.js";
import type { StatusTone } from "../../atoms/StatusDot/index.js";
import { StatusPill } from "../../atoms/StatusPill/index.js";
import {
  DataTable,
  type DataTableColumn,
} from "../../molecules/DataTable/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { KaizenTableView } from "./types.js";

/**
 * Status is the Implementation Plan's vocabulary, and only a promoted Kaizen has
 * one. `done` is neutral rather than healthy: it is a terminal state, not a
 * reassurance, and colouring it green would let a finished pile read as an
 * active one.
 */
const PLAN_STATUS: Record<
  VsmPlanStatus,
  { readonly label: string; readonly tone: StatusTone }
> = {
  "on-track": { label: "On track", tone: "healthy" },
  "at-risk": { label: "At risk", tone: "watch" },
  blocked: { label: "Blocked", tone: "critical" },
  done: { label: "Done", tone: "neutral" },
};

/**
 * Stage comes from the system-owned `inImplementationPlan` flag, never from the
 * presence of a plan row. The two can disagree — a row can be deleted in the plan
 * editor without anyone unpromoting the Kaizen — and when they do, the flag wins:
 * such a row reads *Promoted* with dashes in Status and Due. One source of truth,
 * and a visible gap rather than a silent reclassification.
 */
function stageOf(promoted: boolean): { label: string; tone: StatusTone } {
  return promoted
    ? { label: "Promoted", tone: "info" }
    : { label: "Not promoted", tone: "neutral" };
}

/*
 * Five columns need room, and the threshold is set by the KAIZEN column rather
 * than by overflow. At `@md` the five fit without overflowing — the responsive
 * guardrail passes — but the four fixed columns eat 420 of 492px and the title
 * truncates to a single character, which is a legible layout by the guardrail's
 * measure and a useless one to a reader. `@2xl` is the width at which the title
 * still has ~250px after the fixed columns.
 *
 * Below it the Stage column is hidden and its meaning folds into Status, which
 * is the ONLY place Status is allowed to carry two vocabularies — everywhere
 * else that mixing is what the two-artifact model exists to stop.
 *
 * ## The sixth column, and why it is `@3xl` and not `@lg`
 *
 * "Linked work" is 96px, appended so no header index moves. The threshold is
 * the same rule the fifth column follows: `768 - (100+116+116+84+96) = 256`,
 * the title's budget, matching `672 - 416 = 256` above. (The convention ignores
 * gaps and `px-2` — recomputing with them gives 216 and reads as an error.)
 *
 * A `wide` widget tops out at a 492px container and `full` reaches ~1040px, so
 * `@3xl` means the column appears on a `full` widget and nowhere else. That is
 * correct — 492px cannot hold six columns and a readable title, at any
 * weighting — but it makes the FOLD the path most readers take, not a fallback.
 * It also rules out `@lg` (512px) for that fold: it sits ABOVE the default
 * width, so a reader on the default placement would see neither.
 *
 * Linked work must NOT fold into Status. Status may carry two vocabularies and
 * one exception is already spent on Stage; a third pill would read
 * `Promoted · Blocked · In Progress` with nothing to say which is authoritative.
 * It folds into the title cell instead, text-only.
 */

/**
 * What a row's external work reads as, in one short string.
 *
 * A cell 96px wide has room for one fact, so the shape of the answer changes
 * with the count rather than listing: exactly one link is worth naming and
 * linking to, several are worth counting. The counts always describe the WHOLE
 * set, so "3 linked" never hides work behind a link to one of them.
 */
function linkedWorkLabel(linked: KaizenLinkedWork): string {
  if (linked.total === 1) return linked.key ?? "Linked";
  return `${linked.total} linked`;
}

/**
 * The Linked work readout, shared by the column and the narrow-width fold so the
 * two can never say different things about the same row.
 *
 * `key`/`url` are populated by the server ONLY when there is exactly one link,
 * which is what makes "render a link when there is a url" safe here: it cannot
 * quietly link to one of several.
 */
function LinkedWorkText({
  linked,
}: {
  readonly linked: KaizenLinkedWork;
}): ReactElement {
  const label = linkedWorkLabel(linked);
  // Same rule as `RowBadgeView`, through the same helper: a provider-supplied
  // address we will not link to renders as text, never as an executable href.
  const href = safeExternalHref(linked.url);
  return href === null ? (
    <span className="truncate text-pb-text-secondary">{label}</span>
  ) : (
    <Link
      href={href}
      external
      variant="muted"
      className="truncate"
      aria-label={`${label} — open in the connected system`}
    >
      {label}
    </Link>
  );
}

export function KaizenTableWidget({
  data,
}: WidgetBodyProps<KaizenTableView>): ReactElement {
  const openVsm = useWidgetActions()?.openVsm;
  if (data.rows.length === 0) {
    // Deliberately not "on this map": the same widget sits on Personal Home,
    // where it spans every value stream the reader belongs to, so naming a
    // single map would be wrong on half its surfaces. And "match the current
    // view" rather than "yet": the stage/status filters can empty the list
    // while Kaizens exist, so an empty FILTERED view must not claim none do.
    return (
      <p className="py-4 text-center text-sm italic text-pb-text-muted">
        No Kaizens match the current view.
      </p>
    );
  }
  /* The register is a DataTable (look row OK-84): its own frame, header and
     row paint. Stage hides below `@2xl` and Due below `@md`, both folding into
     Status through `foldInto`, and Owner below `@sm` folds into the Kaizen
     cell; Linked work hides below `@3xl` and folds into the title cell
     by hand, because a Kaizen sent nowhere must add nothing there (the
     column's own cell shows a dash). Hidden columns stay in the DOM, so a
     cell keeps its position in the header row at every width. */
  /* Column widths are today's content widths plus DataTable's 12px cell
     padding on each side (px-3), so owner names and the one-line status and
     stage pills fit as they did in the hand-built grid (100/116/116/84/96px
     of content). */
  const allColumns: ReadonlyArray<DataTableColumn<KaizenRow>> = [
    {
      id: "kaizen",
      header: "Kaizen",
      cell: (row) => (
        <span className="block min-w-0">
          <span className="block truncate text-pb-text">{row.title}</span>
          {/* The value stream's colour rides a small swatch; the button
              itself is neutral (look row OK-07). */}
          <span className="block truncate text-xs">
            <Button
              variant="link"
              tone="neutral"
              size="inline"
              disabled={!openVsm}
              onClick={() => openVsm?.(row.vsmId, row.vsmName)}
              className="max-w-full"
            >
              <StatusDot tint={7} size="xs" />
              <span className="truncate">{row.vsmName}</span>
            </Button>
          </span>
          {/* The narrow-width fold, and it must NOT be gated on a band
              the default placement never reaches. A `wide` widget tops
              out at a 492px container (see the column note above the widget), so an
              `@lg`-and-up fold would be invisible exactly where most
              readers see this table. Below `@3xl` there is no Linked
              work column, so the title cell carries it — unconditionally,
              the same shape the Stage fold already uses. Text only: a
              pill's chrome does not survive the 375px floor. Hidden with
              the column while VSM_JIRA_UI_ENABLED is off. */}
          {!VSM_JIRA_UI_ENABLED || row.linkedWork === null ? null : (
            <span className="block text-xs @3xl:hidden">
              <LinkedWorkText linked={row.linkedWork} />
            </span>
          )}
        </span>
      ),
    },
    {
      id: "owner",
      header: "Owner",
      width: 124,
      /* Folds into the Kaizen cell below `@sm` (a 384px box, PB-5436): on a
         phone the widget's box is ~285px, and Owner and Status alone took
         264px of it, leaving the Kaizen column no room for its header. */
      hideBelow: "sm",
      foldInto: "kaizen",
      cell: (row) => (
        <span
          className={
            row.ownerName === null
              ? "block truncate text-pb-amber-text"
              : "block truncate text-pb-text-secondary"
          }
        >
          {row.ownerName ?? "Unassigned"}
        </span>
      ),
    },
    {
      id: "stage",
      header: "Stage",
      width: 140,
      hideBelow: "2xl",
      foldInto: "status",
      cell: (row) => {
        const stage = stageOf(row.promoted);
        return <StatusPill tone={stage.tone}>{stage.label}</StatusPill>;
      },
    },
    {
      id: "status",
      header: "Status",
      width: 140,
      cell: (row) => {
        const status = row.planStatus ? PLAN_STATUS[row.planStatus] : undefined;
        return status ? (
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        ) : (
          <span
            aria-hidden="true"
            className="hidden text-pb-text-muted @2xl:inline"
          >
            —
          </span>
        );
      },
    },
    {
      id: "due",
      header: "Due",
      width: 108,
      /* Folds under Status below `@md` (a 448px box, PB-5436): with Owner,
         Status and Due fixed at 372px, a narrower card — a VSM monitor beside
         the Player AI rail — left the Kaizen column too little room for its
         own header. */
      hideBelow: "md",
      foldInto: "status",
      cell: (row) => (
        <span
          className={
            row.isOverdue
              ? "text-xs font-semibold text-pb-red-text"
              : "text-xs text-pb-text-secondary"
          }
        >
          {row.planDue ?? "—"}
          {row.isOverdue ? (
            /* A word as well as a colour — meaning never rides on hue
               alone, and a red date says nothing to a screen reader. */
            <span className="ms-1">Overdue</span>
          ) : null}
        </span>
      ),
    },
    {
      id: "linkedWork",
      header: "Linked work",
      width: 120,
      hideBelow: "3xl",
      cell: (row) => (
        <span className="flex min-w-0 items-center text-xs">
          {row.linkedWork === null ? (
            <span aria-hidden="true" className="text-pb-text-muted">
              —
            </span>
          ) : (
            <LinkedWorkText linked={row.linkedWork} />
          )}
        </span>
      ),
    },
  ];
  /* While VSM_JIRA_UI_ENABLED is off, Linked work leaves the table entirely
     rather than hiding by CSS: no header, no cells and no reserved width. */
  const columns = VSM_JIRA_UI_ENABLED
    ? allColumns
    : allColumns.filter((column) => column.id !== "linkedWork");
  return (
    <div className="text-sm">
      <DataTable
        columns={columns}
        data={data.rows}
        getRowId={(row) => row.id}
        density="compact"
        layout="fixed"
        caption="Kaizen table"
      />
      {data.rows.length >= KAIZEN_LIST_ROW_LIMIT ? (
        /* Says only what the dataset guarantees. The rows are ordered by when
           they were last updated and capped at KAIZEN_LIST_ROW_LIMIT — not
           ranked by urgency — and "up to" stays true when exactly the limit
           matched and nothing was cut. The health card is deliberately not
           mentioned: it exists on a value stream's dashboard and not on
           Personal Home, where this same table also renders. */
        <p className="px-2 pt-2 text-dense-2 text-pb-text-muted">
          {`Showing up to ${KAIZEN_LIST_ROW_LIMIT} Kaizens, most recently updated first.`}
        </p>
      ) : null}
    </div>
  );
}
